/**
 * Staff forms (A-05) — shown in modals from the Staff screen.
 *
 * Create: role selector (pharmacist / admin / support); pharmacist adds council + registration #
 * (the latter stored encrypted). On submit the backend creates the account + issues a
 * signup link.
 * Edit: name, phone and email (role and pharmacist registration are fixed).
 */

import { useState } from "react";

import type { Staff } from "../../api/admin";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { Select } from "../../components/ui/Select";

export interface StaffFormValues {
  role: string;
  full_name: string;
  phone: string;
  email: string;
  state_pharmacy_council: string;
  registration_number: string;
}

const ROLES = ["pharmacist", "admin", "support"];

// local@domain.tld — no spaces, one "@", and a TLD of at least 2 letters.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

// Full name: letters, spaces and dots only, and it must start with a letter.
const sanitizeName = (v: string) => v.replace(/[^A-Za-z. ]/g, "").replace(/^[\s.]+/, "");

const EMPTY: StaffFormValues = {
  role: "pharmacist",
  full_name: "",
  phone: "+91",
  email: "",
  state_pharmacy_council: "",
  registration_number: "",
};

export function StaffForm({
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  submitting: boolean;
  error: string | null;
  onSubmit: (values: StaffFormValues) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<StaffFormValues>(EMPTY);
  const set = (patch: Partial<StaffFormValues>) => setForm({ ...form, ...patch });
  // Email is optional — validate the format only when something is entered. The error shows only
  // after the field is left or submit is attempted, not while typing.
  const [emailTouched, setEmailTouched] = useState(false);
  const emailError =
    form.email && !EMAIL_RE.test(form.email) ? "Enter a valid email address (e.g. name@example.com)" : null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (emailError) {
          setEmailTouched(true);
          return;
        }
        onSubmit({ ...form, full_name: form.full_name.trim() });
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-gray-500">
            Role
            <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>
          </span>
          <Select
            value={form.role}
            options={ROLES.map((r) => ({ value: r, label: r[0].toUpperCase() + r.slice(1) }))}
            onChange={(role) => set({ role })}
            required
          />
        </label>
        <Input label="Full name" value={form.full_name} onChange={(v) => set({ full_name: sanitizeName(v) })} required />
        <PhoneInput label="Phone" value={form.phone} onChange={(v) => set({ phone: v })} required />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => set({ email: v.replace(/\s/g, "") })}
          onBlur={() => setEmailTouched(true)}
          error={emailTouched ? emailError : null}
        />
        {form.role === "pharmacist" && (
          <>
            <Input
              label="Pharmacy council"
              value={form.state_pharmacy_council}
              onChange={(v) => set({ state_pharmacy_council: v })}
              required
            />
            <Input
              label="Registration #"
              value={form.registration_number}
              onChange={(v) => set({ registration_number: v })}
              required
            />
          </>
        )}
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <div className="flex gap-2">
        <button className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg" disabled={submitting}>
          Create & send signup link
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

export interface StaffEditValues {
  full_name: string;
  phone: string;
  email: string | null;
}

export function EditStaffForm({
  staff,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  staff: Staff;
  submitting: boolean;
  error: string | null;
  onSubmit: (values: StaffEditValues) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    full_name: staff.full_name,
    phone: staff.phone ?? "+91",
    email: staff.email ?? "",
  });
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  const [emailTouched, setEmailTouched] = useState(false);
  const emailError =
    form.email && !EMAIL_RE.test(form.email) ? "Enter a valid email address (e.g. name@example.com)" : null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (emailError) {
          setEmailTouched(true);
          return;
        }
        onSubmit({ full_name: form.full_name.trim(), phone: form.phone, email: form.email || null });
      }}
      className="space-y-4"
    >
      <p className="text-xs text-gray-400">
        Role: <span className="font-medium text-gray-600">{staff.role}</span>. The phone number is what
        they sign in with.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="Full name" value={form.full_name} onChange={(v) => set({ full_name: sanitizeName(v) })} required />
        <PhoneInput label="Phone" value={form.phone} onChange={(v) => set({ phone: v })} required />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => set({ email: v.replace(/\s/g, "") })}
          onBlur={() => setEmailTouched(true)}
          error={emailTouched ? emailError : null}
        />
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <div className="flex gap-2">
        <button className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50" disabled={submitting}>
          {submitting ? "Saving…" : "Save changes"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

function Input({
  label,
  value,
  onChange,
  onBlur,
  required,
  type = "text",
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  required?: boolean;
  type?: string;
  error?: string | null;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-gray-500">
        {label}
        {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        required={required}
        aria-invalid={!!error}
        className={`rounded-lg border px-3 py-1.5 outline-hidden focus:ring-1 ${
          error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "focus:border-brand focus:ring-brand"
        }`}
      />
      {error && <span className="text-xs text-red-600">{error}</span>}
    </label>
  );
}
